import { useState, useEffect } from 'react';
import { useApp } from '../App';
import api from '../utils/api';

export default function Settings() {
  const { settings, setSettings, toast } = useApp();
  const [form, setForm]   = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);

  useEffect(() => {
    setForm({
      company_name:    settings.company_name    || '',
      company_address: settings.company_address || '',
      company_email:   settings.company_email   || '',
      company_phone:   settings.company_phone   || '',
      default_roe:     settings.default_roe     || '6.77',
      vat_rate:        settings.vat_rate ? String(parseFloat(settings.vat_rate)*100) : '12.5',
      default_validity:settings.default_validity|| '30 days',
      quote_prefix:    settings.quote_prefix    || 'PAS',
    });
  }, [settings]);

  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, vat_rate:String(parseFloat(form.vat_rate||12.5)/100) };
      await api.put('/settings', payload);
      setSettings(s=>({...s,...payload}));
      setSaved(true); setTimeout(()=>setSaved(false),3000);
      toast('Settings saved','success');
    } catch(e) { toast(e.message,'error'); }
    finally { setSaving(false); }
  };

  const Section = ({ eyebrow, title, children }) => (
    <div className="card" style={{ marginBottom:20 }}>
      <div style={{ marginBottom:20 }}>
        <div style={{ fontFamily:'var(--font-mono)',fontSize:9,letterSpacing:'0.14em',textTransform:'uppercase',color:'var(--copper)',marginBottom:4 }}>{eyebrow}</div>
        <div style={{ fontFamily:'var(--font-display)',fontSize:15,fontWeight:700,letterSpacing:'-0.01em' }}>{title}</div>
      </div>
      {children}
    </div>
  );

  return (
    <div className="animate-fade-up" style={{ maxWidth:680 }}>
      <div className="page-header">
        <div>
          <div className="page-eyebrow">System</div>
          <div className="page-title">Settings</div>
          <div className="page-sub">Company details, defaults and configuration</div>
        </div>
        <button className="btn btn-copper" onClick={save} disabled={saving}>
          {saving?'Saving…':saved?'✓ Saved':'Save settings'}
        </button>
      </div>

      <Section eyebrow="Company" title="Company information">
        <div className="field-row">
          <div className="field"><label className="label">Company name</label><input className="input" value={form.company_name||''} onChange={e=>set('company_name',e.target.value)}/></div>
          <div className="field"><label className="label">Phone</label><input className="input" value={form.company_phone||''} onChange={e=>set('company_phone',e.target.value)}/></div>
        </div>
        <div className="field"><label className="label">Address</label><input className="input" value={form.company_address||''} onChange={e=>set('company_address',e.target.value)}/></div>
        <div className="field"><label className="label">Email (shown on quotes)</label><input className="input" type="email" value={form.company_email||''} onChange={e=>set('company_email',e.target.value)}/></div>
      </Section>

      <Section eyebrow="Quotations" title="Quote defaults">
        <div className="field-row-3">
          <div className="field"><label className="label">Quote prefix</label><input className="input" value={form.quote_prefix||''} onChange={e=>set('quote_prefix',e.target.value)} placeholder="PAS"/></div>
          <div className="field"><label className="label">Default ROE (TT$)</label><input className="input" type="number" step="0.01" value={form.default_roe||''} onChange={e=>set('default_roe',e.target.value)}/></div>
          <div className="field"><label className="label">VAT rate (%)</label><input className="input" type="number" step="0.1" value={form.vat_rate||''} onChange={e=>set('vat_rate',e.target.value)}/></div>
        </div>
        <div className="field" style={{ maxWidth:220 }}>
          <label className="label">Default validity</label>
          <select className="select" value={form.default_validity||'30 days'} onChange={e=>set('default_validity',e.target.value)}>
            <option>7 days</option><option>14 days</option><option>30 days</option><option>60 days</option>
          </select>
        </div>
      </Section>

      <Section eyebrow="Email" title="SMTP configuration">
        <div style={{ background:'rgba(196,98,26,0.06)',border:'1px solid rgba(196,98,26,0.15)',borderRadius:10,padding:'12px 16px',fontSize:13,color:'var(--mid)',marginBottom:16,lineHeight:1.6 }}>
          Configure SMTP to send real emails with PDF attachments. For Gmail, generate an <strong>App Password</strong> under Google Account → Security → 2-Step Verification → App Passwords. Set <code style={{ background:'rgba(0,0,0,0.06)',padding:'1px 5px',borderRadius:4 }}>SMTP_PASS</code> in your Railway environment variables.
        </div>
        <div className="field-row">
          <div className="field"><label className="label">SMTP host</label><input className="input" placeholder="smtp.gmail.com" disabled style={{ opacity:0.5 }}/></div>
          <div className="field"><label className="label">SMTP port</label><input className="input" placeholder="587" disabled style={{ opacity:0.5 }}/></div>
        </div>
        <div style={{ fontSize:12,color:'var(--muted)' }}>Set SMTP credentials in Railway → your service → Variables for security.</div>
      </Section>

      <div style={{ background:'rgba(224,59,59,0.06)',border:'1px solid rgba(224,59,59,0.15)',borderRadius:14,padding:'18px 22px' }}>
        <div style={{ fontFamily:'var(--font-display)',fontSize:14,fontWeight:700,marginBottom:12,color:'var(--red-text)' }}>Danger zone</div>
        <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',paddingBottom:12,marginBottom:12,borderBottom:'1px solid rgba(224,59,59,0.1)' }}>
          <div><div style={{ fontWeight:600,fontSize:13 }}>Export all quotes</div><div style={{ fontSize:12,color:'var(--muted)',marginTop:1 }}>Download complete quote history as CSV</div></div>
          <button className="btn btn-outline btn-sm" onClick={()=>toast('Use the Export CSV button on the Quotes page','')}>Export CSV</button>
        </div>
        <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center' }}>
          <div><div style={{ fontWeight:600,fontSize:13,color:'var(--red-text)' }}>Reset quote counter</div><div style={{ fontSize:12,color:'var(--muted)',marginTop:1 }}>Restart numbering from a specific number</div></div>
          <button className="btn btn-danger btn-sm" onClick={()=>toast('Contact your system administrator','')}>Reset counter</button>
        </div>
      </div>
    </div>
  );
}
